import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
// maplibre-gl is loaded from CDN in public/index.html (see comment there).
// We use the global instance because CRA's terser otherwise mangles the
// bundled worker and breaks the map in production builds.
const maplibregl = typeof window !== 'undefined' ? window.maplibregl : null;
import axios from 'axios';
import RoomCalendarPicker from './RoomCalendarPicker';

const API = process.env.REACT_APP_BACKEND_URL;

// Reusable MapLibre renderer for the custom uploaded camp map.
// - `properties` (from /properties/search) provides which rooms are available.
// - Parcels come from /api/camp-map/custom-parcels (admin maps each parcel to a room).
// - Renders the user's style.json as base, then highlights bookable parcels and
//   wires click → onParcelClick(property, room).
// Props:
//   styleUrl: relative URL to style.json (e.g. "/api/uploads/camp-map/active/style.json")
//   center, zoom, minZoom, maxZoom: camera constraints
//   properties: available_rooms data from booking engine
//   onParcelClick(property, room): called when a *bookable* parcel is tapped
//   mobileMode: when true, opens a maplibre popup with parcel info + CTA
//   onDatesChange(newCheckIn, newCheckOut, roomId): called when guest picks new
//     dates from the per-parcel availability calendar (occupied parcel flow)
//   className / style: container styling
//
// Visual states (driven by paint expressions, not by filtering features):
//   - bookable  → parcel is mapped to an available room → click goes to booking
//   - occupied  → parcel is mapped but room not available for current dates →
//                  reddish tint + click opens per-parcel availability calendar
//   - unmapped  → parcel exists in package but no room_id → no overlay, ignored
export default function MapLibreCampMap({
  styleUrl,
  center,
  zoom,
  minZoom,
  maxZoom,
  properties = [],
  onParcelClick,
  onDatesChange,
  onOutOfFilterClick,
  allowedRoomIds = null,
  mobileMode = false,
  className = '',
  showLegend = true,
}) {
  const mapRef = useRef(null);
  const containerRef = useRef(null);
  const [mapReady, setMapReady] = useState(false);
  const [parcels, setParcels] = useState([]); // [{parcel_id, label, room_id, zone_name, fill, stroke, geometry}]
  const [selectedId, setSelectedId] = useState(null);
  const popupRef = useRef(null);
  // When the guest clicks an "occupied" parcel we open this dedicated
  // per-parcel availability calendar instead of going to the booking page.
  // { room_id, label, parcel_id } — null hides the modal.
  const [calendarTarget, setCalendarTarget] = useState(null);

  // Click counter — incremented ONLY on real user clicks so the side-effect
  // useEffect can distinguish between "user just tapped a parcel" (scroll/popup
  // should fire) and "availability auto-refresh mutated parcels/roomMap"
  // (nothing should happen, user keeps scrolling the list freely).
  const [clickSeq, setClickSeq] = useState(0);

  // Refs mirror the latest props/state so the click reaction effect can read
  // current data without depending on them (which would re-fire it on every
  // 10-second availability refresh).
  const parcelsRef = useRef(parcels);
  const roomMapRef = useRef(null);
  const onParcelClickRef = useRef(onParcelClick);
  const mobileModeRef = useRef(mobileMode);

  // Read live brand color so the selected ring matches the active theme.
  const brandColor = useMemo(() => {
    if (typeof window === 'undefined') return '#1D4ED8';
    return getComputedStyle(document.documentElement)
      .getPropertyValue('--color-primary').trim() || '#1D4ED8';
  }, [mapReady]);

  // Fetch parcel mappings (room_id, zone_name) for the active package.
  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/api/camp-map/custom-parcels`).then(res => {
      if (!cancelled) setParcels(res.data || []);
    }).catch(() => { if (!cancelled) setParcels([]); });
    return () => { cancelled = true; };
  }, [styleUrl]);

  // Build a lookup of {room_id → {property, room}} from current search results.
  const roomMap = useMemo(() => {
    const m = new Map();
    properties.forEach(prop => {
      (prop.available_rooms || []).forEach(room => {
        const rid = room.id || room.roomId || room.room_id;
        if (rid != null) m.set(String(rid), { property: prop, room });
      });
    });
    return m;
  }, [properties]);

  // Initialise map only once. We fetch the style ourselves so we can rewrite
  // the sprite URL to absolute (MapLibre rejects relative sprite paths).
  useEffect(() => {
    if (mapRef.current || !containerRef.current || !styleUrl) return;
    let cancelled = false;

    const absUrl = (u) => {
      if (!u || typeof u !== 'string') return u;
      if (/^https?:\/\//.test(u)) return u;
      return `${API}${u.startsWith('/') ? '' : '/'}${u}`;
    };

    const start = async () => {
      let styleObj;
      try {
        const res = await fetch(absUrl(styleUrl));
        styleObj = await res.json();
      } catch (err) {
        console.error('Failed to fetch custom map style:', err);
        return;
      }
      if (cancelled) return;

      // MapLibre requires sprite & glyphs URLs to be absolute. We resolve them
      // against REACT_APP_BACKEND_URL for any /api/uploads/... path the backend
      // returns. External URLs (https://...) are passed through unchanged.
      if (styleObj.sprite) styleObj.sprite = absUrl(styleObj.sprite);
      if (styleObj.glyphs) styleObj.glyphs = absUrl(styleObj.glyphs);

      const map = new maplibregl.Map({
        container: containerRef.current,
        style: styleObj,
        center: center ? [center[1], center[0]] : [15.483, 45.4197],
        zoom: zoom || 17.5,
        minZoom: minZoom || 12,
        maxZoom: maxZoom || 22,
        attributionControl: false,
      });
      map.addControl(new maplibregl.NavigationControl({ visualizePitch: false }), 'top-right');
      map.addControl(new maplibregl.AttributionControl({ compact: true }));
      mapRef.current = map;
      map.on('load', () => setMapReady(true));
    };

    start();
    return () => {
      cancelled = true;
      if (mapRef.current) { mapRef.current.remove(); mapRef.current = null; }
    };
  }, [styleUrl, center, zoom, minZoom, maxZoom]);

  // Add a clickable overlay layer on top of camp-features for highlighting.
  // We don't override the user's base style — we *add* a transparent fill
  // layer above it that lights up the selected/bookable parcels with the
  // brand color, leaving the original camp-features paint visible.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady || parcels.length === 0) return;

    // Build a FeatureCollection of parcel geometries with their metadata so
    // we can drive paint expressions and click handlers from a single source.
    // `state` distinguishes bookable / occupied / wrong_property / unmapped.
    // When `allowedRoomIds` is a non-empty Set the user has filtered by an
    // accommodation type — parcels whose room is NOT in that set render gray
    // and clicking them prompts the user to broaden their search.
    const filterActive = allowedRoomIds && allowedRoomIds.size > 0;
    const features = parcels.map(p => {
      const hasRoom = !!p.room_id;
      const available = hasRoom && roomMap.has(String(p.room_id));
      const inFilter = !filterActive || (hasRoom && allowedRoomIds.has(String(p.room_id)));
      let state;
      if (!hasRoom) state = 'unmapped';
      else if (!inFilter) state = 'wrong_property';
      else if (available) state = 'bookable';
      else state = 'occupied';
      return {
        type: 'Feature',
        properties: {
          parcel_id: p.parcel_id,
          label: String(p.label || ''),
          room_id: String(p.room_id || ''),
          zone_name: p.zone_name || '',
          bookable: state === 'bookable',
          occupied: state === 'occupied',
          wrong_zone: state === 'wrong_property',
          state,
        },
        geometry: p.geometry,
      };
    });
    const data = { type: 'FeatureCollection', features };

    const SRC = 'booking-parcels';
    if (map.getSource(SRC)) {
      map.getSource(SRC).setData(data);
    } else {
      map.addSource(SRC, { type: 'geojson', data });
      // Wrong-zone overlay — translucent gray on parcels that DON'T match the
      // user's active zone filter. Rendered first so red/brand layers stay on
      // top. Click on a gray parcel opens a popup that nudges the user back to
      // the homepage to broaden their search.
      map.addLayer({
        id: 'booking-parcels-wrong-zone',
        type: 'fill',
        source: SRC,
        filter: ['==', ['get', 'wrong_zone'], true],
        paint: {
          'fill-color': '#6B7280',
          'fill-opacity': 0.45,
        },
      });
      map.addLayer({
        id: 'booking-parcels-wrong-zone-outline',
        type: 'line',
        source: SRC,
        filter: ['==', ['get', 'wrong_zone'], true],
        paint: {
          'line-color': '#4B5563',
          'line-width': 1.2,
          'line-opacity': 0.6,
        },
      });
      // Occupied overlay — translucent red tint on parcels mapped to a room
      // that's NOT available for the current dates. Renders BENEATH the
      // selected-highlight layers so the user's selection ring (brand color)
      // remains the visually dominant cue.
      map.addLayer({
        id: 'booking-parcels-occupied',
        type: 'fill',
        source: SRC,
        filter: ['==', ['get', 'occupied'], true],
        paint: {
          'fill-color': '#DC2626',
          'fill-opacity': 0.35,
        },
      });
      map.addLayer({
        id: 'booking-parcels-occupied-outline',
        type: 'line',
        source: SRC,
        filter: ['==', ['get', 'occupied'], true],
        paint: {
          'line-color': '#B91C1C',
          'line-width': 1.5,
          'line-opacity': 0.7,
        },
      });
      // Selected fill (brand color) — only the selected parcel
      map.addLayer({
        id: 'booking-parcels-selected',
        type: 'fill',
        source: SRC,
        filter: ['==', ['get', 'parcel_id'], ''],
        paint: {
          'fill-color': brandColor,
          'fill-opacity': 0.4,
        },
      });
      map.addLayer({
        id: 'booking-parcels-selected-outline',
        type: 'line',
        source: SRC,
        filter: ['==', ['get', 'parcel_id'], ''],
        paint: {
          'line-color': brandColor,
          'line-width': 3,
        },
      });
      // Invisible hit layer covering bookable, occupied AND wrong-zone
      // parcels — clicks on bookable parcels go through normal booking flow;
      // clicks on occupied parcels open the per-parcel availability calendar;
      // clicks on wrong-zone parcels (gray) open a "broaden your search" prompt
      // routed by the parent via onOutOfFilterClick. Unmapped parcels stay
      // non-interactive.
      map.addLayer({
        id: 'booking-parcels-hit',
        type: 'fill',
        source: SRC,
        filter: ['any',
          ['==', ['get', 'bookable'], true],
          ['==', ['get', 'occupied'], true],
          ['==', ['get', 'wrong_zone'], true],
        ],
        paint: { 'fill-color': '#000', 'fill-opacity': 0 },
      });
      // Number label — bold dark text centered on each parcel polygon. Halo
      // keeps digits legible across green/red/blue base parcels. We DO NOT
      // force overlap so MapLibre auto-hides labels on parcels that are too
      // small at the current zoom — prevents the mess of overlapping numbers
      // when the user pans/pinches out on mobile.
      map.addLayer({
        id: 'booking-parcels-label',
        type: 'symbol',
        source: SRC,
        minzoom: 15.5,
        layout: {
          'text-field': ['get', 'label'],
          'text-size': [
            'interpolate', ['linear'], ['zoom'],
            15.5, 9,
            17, 11,
            18.5, 14,
            20, 18,
            22, 22,
          ],
          'text-font': ['Noto Sans Bold', 'Open Sans Bold', 'Arial Unicode MS Bold'],
          'text-allow-overlap': false,
          'text-ignore-placement': false,
          'text-padding': 2,
          'symbol-placement': 'point',
        },
        paint: {
          'text-color': '#111827',
          'text-halo-color': 'rgba(255,255,255,0.95)',
          'text-halo-width': 1.4,
          'text-halo-blur': 0.4,
        },
      });

      map.on('click', 'booking-parcels-hit', (e) => {
        const f = e.features?.[0];
        if (!f) return;
        const pid = f.properties.parcel_id;
        setSelectedId(pid);
        // Bump click counter so the side-effect useEffect knows this was a
        // real user gesture (vs. an availability auto-refresh re-render).
        // Also bumped if the user re-taps the same parcel — selectedId is
        // unchanged but clickSeq still increments → effect re-fires.
        setClickSeq((s) => s + 1);
      });
      map.on('mouseenter', 'booking-parcels-hit', () => {
        map.getCanvas().style.cursor = 'pointer';
      });
      map.on('mouseleave', 'booking-parcels-hit', () => {
        map.getCanvas().style.cursor = '';
      });
    }
  }, [mapReady, parcels, roomMap, brandColor, allowedRoomIds]);

  // Update selected filter when selection changes.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady) return;
    const filter = ['==', ['get', 'parcel_id'], selectedId || '__none__'];
    if (map.getLayer('booking-parcels-selected')) map.setFilter('booking-parcels-selected', filter);
    if (map.getLayer('booking-parcels-selected-outline')) map.setFilter('booking-parcels-selected-outline', filter);
  }, [selectedId, mapReady]);

  // Mirror latest props/state into refs so the click-reaction effect below
  // can always read the freshest data without depending on them as effect
  // deps. If `parcels`/`roomMap` were direct deps, every 10-second
  // availability auto-refresh would re-fire the side-effect and the page
  // would auto-scroll back to the selected parcel — exactly what the user
  // complained about.
  useEffect(() => { parcelsRef.current = parcels; }, [parcels]);
  useEffect(() => { roomMapRef.current = roomMap; }, [roomMap]);
  useEffect(() => { onParcelClickRef.current = onParcelClick; }, [onParcelClick]);
  useEffect(() => { mobileModeRef.current = mobileMode; }, [mobileMode]);
  // We also need the latest "out-of-filter" handler + active allowed room set
  // so the click reaction can decide whether a parcel was intentionally
  // filtered out (gray) versus simply occupied (red).
  const onOutOfFilterClickRef = useRef(onOutOfFilterClick);
  const allowedRoomIdsRef = useRef(allowedRoomIds);
  useEffect(() => { onOutOfFilterClickRef.current = onOutOfFilterClick; }, [onOutOfFilterClick]);
  useEffect(() => { allowedRoomIdsRef.current = allowedRoomIds; }, [allowedRoomIds]);

  // Side-effect of selection: route to the right interaction based on the
  // parcel's state.
  //   - bookable: notify parent (desktop scroll-to-room / mobile popup)
  //   - occupied: open the per-parcel availability calendar
  //   - wrong_property: open a "broaden your search" popup (gray parcels)
  //   - unmapped: ignored (no listener on those parcels)
  // Fires ONLY when clickSeq changes (real user clicks); availability
  // refreshes that mutate parcels/roomMap no longer re-trigger this.
  useEffect(() => {
    if (clickSeq === 0) return; // initial mount, no user click yet
    if (!selectedId) return;
    const parcels = parcelsRef.current;
    const roomMap = roomMapRef.current;
    const onParcelClick = onParcelClickRef.current;
    const mobileMode = mobileModeRef.current;
    const onOutOfFilterClick = onOutOfFilterClickRef.current;
    const allowedSet = allowedRoomIdsRef.current;

    const parcel = parcels.find(p => p.parcel_id === selectedId);
    if (!parcel) return;
    const hasRoom = !!parcel.room_id;
    const available = hasRoom && roomMap && roomMap.has(String(parcel.room_id));
    const filterActive = allowedSet && allowedSet.size > 0;
    const outOfFilter = filterActive && hasRoom && !allowedSet.has(String(parcel.room_id));

    if (outOfFilter) {
      // WRONG PROPERTY (gray) — show popup that suggests broadening the
      // search. The CTA delegates to the parent which typically navigates the
      // user back to the homepage so they can switch accommodation type.
      if (mapRef.current && parcel.geometry) {
        const ctr = centroidOf(parcel.geometry);
        if (popupRef.current) popupRef.current.remove();
        const node = document.createElement('div');
        node.innerHTML = renderWrongPropertyPopupHtml(parcel);
        popupRef.current = new maplibregl.Popup({ closeButton: true, offset: 10, maxWidth: '300px' })
          .setLngLat(ctr).setDOMContent(node).addTo(mapRef.current);
        const btn = node.querySelector('[data-action="broaden-search"]');
        if (btn) {
          btn.addEventListener('click', () => {
            popupRef.current?.remove();
            if (onOutOfFilterClick) {
              onOutOfFilterClick({
                room_id: String(parcel.room_id || ''),
                parcel_id: parcel.parcel_id,
                label: parcel.label,
              });
            }
          });
        }
      }
      return;
    }

    if (hasRoom && !available) {
      // OCCUPIED — show a small map popup explaining why, plus a CTA that
      // opens the dedicated availability calendar for this exact parcel.
      if (mapRef.current && parcel.geometry) {
        const ctr = centroidOf(parcel.geometry);
        if (popupRef.current) popupRef.current.remove();
        const node = document.createElement('div');
        node.innerHTML = renderOccupiedPopupHtml(parcel);
        popupRef.current = new maplibregl.Popup({ closeButton: true, offset: 10, maxWidth: '280px' })
          .setLngLat(ctr).setDOMContent(node).addTo(mapRef.current);
        const btn = node.querySelector('[data-action="open-calendar"]');
        if (btn) {
          btn.addEventListener('click', () => {
            popupRef.current?.remove();
            setCalendarTarget({
              room_id: String(parcel.room_id),
              label: parcel.label,
              parcel_id: parcel.parcel_id,
            });
          });
        }
      }
      return;
    }

    if (!available) return;
    const pr = roomMap.get(String(parcel.room_id));
    if (!pr) return;
    if (!mobileMode && onParcelClick) {
      onParcelClick(pr.property, pr.room);
    }
    if (mobileMode && mapRef.current && parcel.geometry) {
      const center = centroidOf(parcel.geometry);
      if (popupRef.current) popupRef.current.remove();
      const node = document.createElement('div');
      node.innerHTML = renderPopupHtml(parcel, pr.room);
      popupRef.current = new maplibregl.Popup({ closeButton: true, offset: 10, maxWidth: '260px' })
        .setLngLat(center).setDOMContent(node).addTo(mapRef.current);
      const btn = node.querySelector('[data-action="view-more"]');
      if (btn && onParcelClick) {
        btn.addEventListener('click', () => onParcelClick(pr.property, pr.room));
      }
    }
  }, [clickSeq]);

  // Zone legend (dynamic — groups bookable parcels by fill+zone_name)
  const legend = useMemo(() => {
    const m = new Map();
    parcels.forEach(p => {
      if (!p.room_id || !roomMap.has(String(p.room_id))) return;
      const fill = p.fill || '#22C55E';
      const name = (p.zone_name || '').trim() || 'Slobodno';
      const key = `${fill}|${name}`;
      if (!m.has(key)) m.set(key, { fill, name, count: 0 });
      m.get(key).count += 1;
    });
    return Array.from(m.values()).sort((a, b) => b.count - a.count);
  }, [parcels, roomMap]);

  const availableCount = useMemo(
    () => parcels.filter(p => p.room_id && roomMap.has(String(p.room_id))).length,
    [parcels, roomMap]
  );
  const occupiedCount = useMemo(
    () => parcels.filter(p => p.room_id && !roomMap.has(String(p.room_id))).length,
    [parcels, roomMap]
  );
  const outOfFilterCount = useMemo(() => {
    if (!allowedRoomIds || allowedRoomIds.size === 0) return 0;
    return parcels.filter(p => p.room_id && !allowedRoomIds.has(String(p.room_id))).length;
  }, [parcels, allowedRoomIds]);

  return (
    <div className={`flex flex-col h-full ${mobileMode ? '' : 'p-3 sm:p-5 gap-3'} ${className}`}
      data-testid="maplibre-camp-map">
      {showLegend && legend.length > 0 && !mobileMode && (
        <div className="bg-white border rounded-lg p-3 shadow-sm" data-testid="zone-legend">
          <p className="text-xs font-semibold uppercase tracking-wide mb-2"
            style={{ color: 'var(--color-text-muted)' }}>Legenda zona</p>
          <div className="flex flex-wrap gap-x-4 gap-y-2 text-sm">
            {legend.map((z, i) => (
              <span key={i} className="flex items-center gap-2" style={{ color: 'var(--color-text-primary)' }}>
                <span className="inline-block w-4 h-4 rounded border border-white shadow"
                  style={{ background: z.fill }} />
                <span className="font-medium">{z.name}</span>
                <span className="text-xs" style={{ color: 'var(--color-text-muted)' }}>({z.count})</span>
              </span>
            ))}
            <span className="flex items-center gap-2" style={{ color: 'var(--color-text-primary)' }}>
              <span className="inline-block w-4 h-4 rounded border border-white shadow"
                style={{ background: '#DC2626', opacity: 0.65 }} />
              <span className="font-medium">Zauzeto</span>
              {occupiedCount > 0 && (
                <span className="text-xs" style={{ color: 'var(--color-text-muted)' }}>({occupiedCount})</span>
              )}
            </span>
            {outOfFilterCount > 0 && (
              <span className="flex items-center gap-2" style={{ color: 'var(--color-text-primary)' }}>
                <span className="inline-block w-4 h-4 rounded border border-white shadow"
                  style={{ background: '#6B7280', opacity: 0.75 }} />
                <span className="font-medium">Drugi tip smještaja</span>
                <span className="text-xs" style={{ color: 'var(--color-text-muted)' }}>({outOfFilterCount})</span>
              </span>
            )}
            <span className="flex items-center gap-2 ml-auto" style={{ color: 'var(--color-text-primary)' }}>
              <span className="inline-block w-4 h-4 rounded border-2 border-white"
                style={{ background: brandColor }} />
              <span className="font-medium">Odabrano</span>
            </span>
          </div>
        </div>
      )}

      <div ref={containerRef}
        className={`relative ${mobileMode ? 'flex-1' : 'flex-1 rounded-xl shadow-lg overflow-hidden border'}`}
        style={{ minHeight: mobileMode ? '100%' : '420px', borderColor: 'var(--color-border)' }} />

      {!mobileMode && (
        <div className="absolute bottom-6 right-6 bg-white/95 backdrop-blur px-3 py-1.5 rounded-full text-xs font-semibold shadow z-[10] pointer-events-none"
          style={{ color: 'var(--color-text-primary)' }}>
          {availableCount} dostupno
          {occupiedCount > 0 && (
            <span className="ml-2" style={{ color: '#DC2626' }}>· {occupiedCount} zauzeto</span>
          )}
        </div>
      )}

      {/* Per-parcel availability calendar — opens when guest clicks an
          occupied (red) parcel. Reuses RoomCalendarPicker so the calendar
          shows only this room's availability. Confirm → update search dates. */}
      {calendarTarget && (
        <RoomCalendarPicker
          roomId={calendarTarget.room_id}
          roomName={`Parcela ${calendarTarget.label}`}
          initialCheckIn={null}
          initialCheckOut={null}
          onConfirm={(newCheckIn, newCheckOut) => {
            if (onDatesChange) {
              onDatesChange(newCheckIn, newCheckOut, calendarTarget.room_id);
            }
            setCalendarTarget(null);
          }}
          onCancel={() => setCalendarTarget(null)}
        />
      )}
    </div>
  );
}

function centroidOf(geom) {
  // Geometry comes from style.json as GeoJSON [lng, lat]. Compute the mean
  // vertex position; good enough for popup anchor on typical parcel polygons.
  if (!geom) return [15.483, 45.4197];
  const coords = (geom.type === 'Polygon' ? geom.coordinates[0] : geom.coordinates) || [];
  if (coords.length === 0) return [15.483, 45.4197];
  let lng = 0, lat = 0, n = 0;
  coords.forEach(c => { lng += c[0]; lat += c[1]; n += 1; });
  return [lng / n, lat / n];
}

function renderPopupHtml(parcel, room) {
  const base = parseFloat(room?.base_price ?? room?.price);
  const price = isFinite(base) ? `od €${Math.round(base)}` : '';
  const zone = (parcel.zone_name || '').trim();
  const num = String(parcel.label || '#');
  const color = parcel.fill || '#22C55E';
  return `
    <div class="w-[220px]" data-testid="parcel-popup">
      <div style="display:flex;align-items:center;gap:8px;margin-bottom:4px;">
        <span style="display:inline-flex;align-items:center;justify-content:center;width:28px;height:28px;border-radius:6px;color:#fff;font-weight:700;font-size:12px;background:${color};">${num}</span>
        <p style="font-weight:600;font-size:14px;margin:0;line-height:1.25;color:var(--color-text-primary);">Parcela ${num}</p>
      </div>
      <div style="display:flex;justify-content:space-between;align-items:baseline;margin:6px 0 10px;">
        <span style="font-size:12px;color:var(--color-text-muted);">${zone}</span>
        <span style="font-size:14px;font-weight:700;color:var(--color-primary);">${price}</span>
      </div>
      <button data-action="view-more" data-testid="parcel-popup-view-more"
        style="width:100%;padding:8px 12px;background:var(--color-primary);color:#fff;border:0;border-radius:6px;font-size:14px;font-weight:600;cursor:pointer;">
        Vidi više
      </button>
    </div>
  `;
}

// Popup HTML shown when the guest clicks an occupied (red) parcel — explains
// the parcel is taken for current dates and offers a CTA to open the per-room
// availability calendar so they can pick alternative dates.
function renderOccupiedPopupHtml(parcel) {
  const num = String(parcel.label || '#');
  const zone = (parcel.zone_name || '').trim();
  return `
    <div class="w-[240px]" data-testid="parcel-popup-occupied">
      <div style="display:flex;align-items:center;gap:8px;margin-bottom:6px;">
        <span style="display:inline-flex;align-items:center;justify-content:center;width:28px;height:28px;border-radius:6px;color:#fff;font-weight:700;font-size:12px;background:#DC2626;">${num}</span>
        <div>
          <p style="font-weight:600;font-size:14px;margin:0;line-height:1.2;color:var(--color-text-primary);">Parcela ${num}</p>
          <p style="font-size:11px;margin:2px 0 0;color:#DC2626;font-weight:600;">Zauzeto za odabrane datume</p>
        </div>
      </div>
      ${zone ? `<p style="font-size:12px;margin:0 0 10px;color:var(--color-text-muted);">${zone}</p>` : ''}
      <button data-action="open-calendar" data-testid="parcel-popup-open-calendar"
        style="width:100%;padding:8px 12px;background:var(--color-primary);color:#fff;border:0;border-radius:6px;font-size:13px;font-weight:600;cursor:pointer;">
        Pogledaj kalendar dostupnosti
      </button>
    </div>
  `;
}


// Popup HTML shown when the guest clicks a gray parcel (one that belongs to
// a different accommodation type than the one currently filtered). Nudges the
// user back to the homepage to broaden their search instead of going to the
// booking page.
function renderWrongPropertyPopupHtml(parcel) {
  const num = String(parcel.label || '#');
  return `
    <div class="w-[260px]" data-testid="parcel-popup-wrong-zone">
      <div style="display:flex;align-items:center;gap:8px;margin-bottom:6px;">
        <span style="display:inline-flex;align-items:center;justify-content:center;width:28px;height:28px;border-radius:6px;color:#fff;font-weight:700;font-size:12px;background:#6B7280;">${num}</span>
        <div>
          <p style="font-weight:600;font-size:14px;margin:0;line-height:1.2;color:var(--color-text-primary);">Parcela ${num}</p>
          <p style="font-size:11px;margin:2px 0 0;color:#6B7280;font-weight:600;">Drugi tip smještaja</p>
        </div>
      </div>
      <p style="font-size:12px;margin:6px 0 10px;color:var(--color-text-muted);">
        Ova parcela nije u tipu smještaja koji ste tražili. Promijenite pretragu da bi je uključili.
      </p>
      <button data-action="broaden-search" data-testid="parcel-popup-broaden-search"
        style="width:100%;padding:8px 12px;background:var(--color-primary);color:#fff;border:0;border-radius:6px;font-size:13px;font-weight:600;cursor:pointer;">
        Promijeni pretragu
      </button>
    </div>
  `;
}
